function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _object_without_properties(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
import { useQuery as useApolloQuery } from "@apollo/client";
import { usePerformanceMonitorTrack } from "@sail/observability";
import { useMemoizedValue } from "@sail/react";
import { useCallback, useContext, useEffect, useMemo, useRef } from "react";
import useGetApolloContext from "../../internal/apollo/useGetApolloContext";
import { DEFAULT_IGNORE_TRACKING_FIELDS, DEFAULT_TRACK_ACCESSES_SAMPLE_RATE, useTrackGraphQlFieldAccesses } from "../../internal/deadwood/deadwood";
import PrefetchEnvContext from "../../internal/prefetching/PrefetchEnvContext";
import { decrementPrefetchCounter } from "../../internal/prefetching/decrementPrefetchCounter";
import ensureCorrectFetchPolicy from "../../internal/prefetching/ensureCorrectFetchPolicy";
import ensureCorrectNextFetchPolicy from "../../internal/prefetching/ensureCorrectNextFetchPolicy";
import useDefaultFetchPolicy from "../../internal/prefetching/useDefaultFetchPolicy";
import usePrefetchCounter from "../../internal/prefetching/usePrefetchCounter";
import { getOperationName } from "../../internal/shared/utils/gql";
import useDataConfig from "../useDataConfig";
const EMPTY_OPTIONS = {};
/**
 * @see https://sail.stripe.me/apis/data/useQuery
 */ export default function useQuery(/**
   * The GraphQL query that is going to be executed. This must be created with the
   * [`gql`](/apis/data/gql) or
   * [`graphql`](/apis/data/graphql) template literal.
   */ query, /**
   * The options to pass to the query. This argument is optional in the case the query does not
   * have any required variable or [permission](/apis/data/permissions).
   */ ...optionsRest) {
    var _dataConfig_graphql_prefetch, _dataConfig_graphql;
    const { isPrefetchMode } = useContext(PrefetchEnvContext);
    const options = optionsRest[0] || EMPTY_OPTIONS;
    const performanceMonitorTrack = usePerformanceMonitorTrack();
    const defaultFetchPolicy = useDefaultFetchPolicy();
    const dataConfig = useDataConfig();
    const operationName = useMemo(()=>{
        return getOperationName(query) || '';
    }, [
        query
    ]);
    let correctFetchPolicy = options.fetchPolicy || defaultFetchPolicy;
    let correctNextFetchPolicy = options.nextFetchPolicy;
    if (isPrefetchMode) {
        correctFetchPolicy = ensureCorrectFetchPolicy(correctFetchPolicy);
    }
    correctNextFetchPolicy = ensureCorrectNextFetchPolicy(correctFetchPolicy, correctNextFetchPolicy);
    const optionsWithCorrectFetchPolicy = useMemo(()=>{
        const updatedOptions = _object_spread_props(_object_spread({}, options), {
            fetchPolicy: correctFetchPolicy
        });
        if (correctNextFetchPolicy) {
            updatedOptions.nextFetchPolicy = correctNextFetchPolicy;
        }
        return updatedOptions;
    }, [
        options,
        correctFetchPolicy,
        correctNextFetchPolicy
    ]);
    const operationContext = useGetApolloContext(optionsWithCorrectFetchPolicy);
    const queryOptions = useMemo(()=>{
        return _object_spread_props(_object_spread({}, optionsWithCorrectFetchPolicy), {
            context: operationContext
        });
    }, [
        operationContext,
        optionsWithCorrectFetchPolicy
    ]);
    const memoizedVariables = useMemoizedValue(queryOptions.variables);
    usePrefetchCounter({
        operationContext,
        query,
        variables: queryOptions.variables,
        skip: queryOptions.skip || false
    });
    const execResult = useApolloQuery(query, queryOptions);
    const trackRef = useRef(null);
    const startTrack = useCallback(()=>{
        if (!trackRef.current) {
            trackRef.current = performanceMonitorTrack(`GraphQL: ${operationName}`);
        }
    }, [
        operationName,
        performanceMonitorTrack
    ]);
    const stopTrack = useCallback(()=>{
        if (trackRef.current) {
            trackRef.current();
            trackRef.current = null;
        }
    }, []);
    if (!isPrefetchMode) {
        if (execResult.loading) {
            // eslint-disable-next-line react-hooks/refs
            startTrack();
        } else {
            // eslint-disable-next-line react-hooks/refs
            stopTrack();
        }
    }
    useEffect(()=>{
        return stopTrack;
    }, [
        stopTrack
    ]);
    var _options_trackAccesses;
    const _useTrackGraphQlFieldAccesses = useTrackGraphQlFieldAccesses(execResult, {
        query,
        sampleRate: (_options_trackAccesses = options.trackAccesses) !== null && _options_trackAccesses !== void 0 ? _options_trackAccesses : DEFAULT_TRACK_ACCESSES_SAMPLE_RATE,
        ignoreFields: DEFAULT_IGNORE_TRACKING_FIELDS
    }), { client, refetch: originalRefetch } = _useTrackGraphQlFieldAccesses, data = _object_without_properties(_useTrackGraphQlFieldAccesses, [
        "client",
        "refetch"
    ]);
    const refetchWithDecrementPrefetchCounter = useCallback((variables)=>{
        const mergedVariables = typeof variables !== 'undefined' ? _object_spread({}, memoizedVariables, variables) : memoizedVariables;
        decrementPrefetchCounter({
            service: operationContext.service,
            component: operationContext.component,
            project: operationContext.queryProject,
            operationName,
            variables: mergedVariables
        });
        return originalRefetch(variables);
    }, [
        originalRefetch,
        operationName,
        memoizedVariables,
        operationContext.service,
        operationContext.component,
        operationContext.queryProject
    ]);
    const shouldDecrementPrefetchCounterWhenRefetching = (_dataConfig_graphql = dataConfig.graphql) === null || _dataConfig_graphql === void 0 ? void 0 : (_dataConfig_graphql_prefetch = _dataConfig_graphql.prefetch) === null || _dataConfig_graphql_prefetch === void 0 ? void 0 : _dataConfig_graphql_prefetch.enableMismatchMetrics;
    return shouldDecrementPrefetchCounterWhenRefetching ? _object_spread({
        refetch: refetchWithDecrementPrefetchCounter
    }, data) : _object_spread({
        refetch: originalRefetch
    }, data);
}

//# sourceMappingURL=useQuery.js.map